import {Link} from '@jobseeker/lotus/elements';
import ButtonV2 from '@jobseeker/lotus/elements/ButtonV2';
import UploadInput from '@jobseeker/lotus/elements/UploadInput';
import Loader from '@jobseeker/lotus/components/Loader';
import {Box, Skeleton, Typography} from '@mui/material';
import {showErrorToast} from '@jobseeker/common-core/utils';
import {
  truncateString,
  getFileBlobFromUrl,
  getFileNameFromFileUrl,
} from '@jobseeker/lotus/utils';
import {BaseSyntheticEvent, useEffect, useRef, useState} from 'react';
import {Image} from '@jobseeker/lotus/components/Image';
import {ResumeAttached, HiristResumeAttached} from '@jobseeker/lotus/icons';
const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

type UploadFileProps = {
  title: string;
  buttonText: string;
  submitFile: boolean;
  changeFile?: boolean;
  handleSubmit: (formData: FormData) => Promise<void>;
  handleFileConfirm?: () => void;
  handleTracking?: () => void;
  formFileName?: string;
  initialFile?: string | null;
  defaultFileName?: string;
  uploadTimeStamp?: string;
  allowedFileSize?: number;
  allowedFileFormat?: string;
  alertMessage?: string;
  newUploadText?: string;
  downloadText?: string;
  setOpenPanel1?: any;
  setOpenPanel3?: any;
  setButtonEnable?: any;
  setUbaTrigger?: any;
  suppressErrorToast?: boolean;
  fileSizeErrorMessage?: string;
  fileTypeErrorMessage?: string;
};

const UploadFileV2 = ({
  title,
  buttonText,
  submitFile,
  changeFile,
  handleSubmit,
  handleFileConfirm,
  formFileName = 'resume_file',
  initialFile,
  defaultFileName,
  uploadTimeStamp,
  allowedFileSize = 2097152,
  allowedFileFormat = '.doc,.docx,.pdf',
  alertMessage = 'Accepted Format includes PDF, DOC & DOCX upto 2MB',
  newUploadText = 'Upload new resume',
  downloadText = 'Download',
  setOpenPanel1,
  setOpenPanel3,
  setButtonEnable,
  setUbaTrigger,
  suppressErrorToast = false,
  fileSizeErrorMessage = 'This file size exceed the maximum limit of 2MB.',
  fileTypeErrorMessage,
}: UploadFileProps) => {
  const NO_FILE_NAME = 'No file chosen';
  const [fileName, setFileName] = useState(NO_FILE_NAME);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<Blob>();
  const [error, setError] = useState<string>('');
  const hiddenFileInput = useRef<HTMLInputElement>(null);
  const hiddenFileReInput = useRef<HTMLInputElement>(null);
  const [sanitisedFileName, setSanitisedFileName] = useState('');
  const [enableCta, setEnableCta] = useState(false);

  useEffect(() => {
    setSanitisedFileName(
      `${defaultFileName}.${fileName.split('.').pop()?.toLowerCase()}`
    );
  }, [defaultFileName, fileName]);

  const reportValidationError = (message: string, hint: string) => {
    if (suppressErrorToast) {
      setError(message);
      return;
    }
    setError(hint);
    try {
      showErrorToast(message);
    } catch {}
  };

  const handleFileUpload = async (event: BaseSyntheticEvent) => {
    event.preventDefault();
    const fileUploaded = event?.target?.files?.length
      ? event?.target?.files[0]
      : null;

    if (fileUploaded) {
      const validExtensions = allowedFileFormat.toLowerCase().split(',');
      const fileNameWithoutExtension =
        fileUploaded.name.substring(0, fileUploaded.name.lastIndexOf('.')) ||
        fileUploaded.name;
      const fileExtension = fileUploaded.name.split('.').pop()?.toLowerCase();
      if (fileUploaded.size > allowedFileSize) {
        reportValidationError(fileSizeErrorMessage, 'filesize');
      } else if (
        !fileExtension ||
        !validExtensions.includes(`.${fileExtension}`)
      ) {
        reportValidationError(fileTypeErrorMessage ?? alertMessage, 'invalid');
      } else {
        const normalizedFileName = `${fileNameWithoutExtension}.${fileExtension}`;
        setFileName(normalizedFileName);
        const buffer = await fileUploaded.arrayBuffer();
        const inMemoryFile = new File([buffer], fileUploaded.name, {
          type: fileUploaded.type,
        });
        setFile(inMemoryFile);
        setError('');
        handleFileConfirm && handleFileConfirm();
      }
    } else {
      // setFileName(NO_FILE_NAME);
    }
  };

  const checkAndDownloadFile = async () => {
    if (file) {
      const url = window.URL.createObjectURL(file);
      const a = document.createElement('a');
      document.body.appendChild(a);
      a.style.display = 'none';
      a.href = url;
      a.download = sanitisedFileName;
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    }
  };

  const onSubmit = async () => {
    setLoading(true);
    if (typeof file !== 'object') {
      if (error) {
        setLoading(false);
        return;
      }
      setError('nofile');
      setLoading(false);
      return;
    } else if (error) {
      setLoading(false);
      return;
    }
    const formData = new FormData();
    formData.append(formFileName, file, fileName);
    try {
      await handleSubmit(formData);
      setLoading(false);
    } catch (error: any) {
      setError(error?.message);
      setLoading(false);
      return;
    }
  };

  useEffect(() => {
    if (submitFile) {
      (async () => {
        await onSubmit();
      })();
    }
  }, [submitFile, changeFile]);

  const updateFileFromUrl = async (fileUrl: string) => {
    const fileBlob = await getFileBlobFromUrl(fileUrl);
    setFile(fileBlob);
  };

  useEffect(() => {
    if (initialFile?.length) {
      setFileName(getFileNameFromFileUrl(initialFile));
      updateFileFromUrl(initialFile);
    }
  }, [initialFile]);

  return (
    <Box>
      {title && (
        <Typography variant="subtitle2" lineHeight={'18px'}>
          {title}
        </Typography>
      )}
      {initialFile != null ? (
        initialFile.length ? (
          <Box
            width={'100%'}
            display={'flex'}
            justifyContent={'space-between'}
            alignItems={'center'}
            py={{xxs: 1.375, md: 2}}
            px={{xxs: 1.375, md: 2.375}}
            border={'1px solid #E5E5E5'}
            borderRadius={5.5}
            mt={1}
            gap={1.375}
            sx={{
              ':hover': {
                borderColor: '#33333390',
              },
            }}
          >
            <Box
              display={'flex'}
              justifyContent={'flex-start'}
              alignItems={'flex-start'}
              width={'100%'}
            >
              <Box
                display={{xxs: 'none', md: 'flex'}}
                justifyContent={'flex-start'}
                alignItems={'flex-start'}
                width={24}
                height={24}
                mt={'2px'}
              >
                <Image
                  src={isAppIimjobs ? ResumeAttached : HiristResumeAttached}
                  width={24}
                  height={24}
                  alt="resume"
                />
              </Box>
              <Box
                display={'flex'}
                flexDirection={'column'}
                justifyContent={'flex-start'}
                alignItems={'flex-start'}
                gap={0.6}
                width={'100%'}
                marginLeft={{xxs: '3px', md: '11px'}}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  width="100%"
                >
                  <Typography
                    color="secondary.main"
                    variant="body2"
                    lineHeight="16px"
                  >
                    {loading ? <Loader size={12} /> : `${sanitisedFileName}`}
                  </Typography>
                  <Typography
                    component={'label'}
                    color="primary.main"
                    variant="subtitle3"
                    lineHeight="16px"
                    sx={{
                      cursor: 'pointer',
                    }}
                    onClick={checkAndDownloadFile}
                    whiteSpace="nowrap"
                  >
                    {downloadText}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    color: '#7E7E7E',
                    WebkitTextStrokeWidth: '1px',
                    WebkitTextStrokeColor: 'rgba(145, 184, 121, 0.16)',
                    fontSize: '12px',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    lineHeight: '21px',
                  }}
                >
                  {uploadTimeStamp?.length && uploadTimeStamp != 'Invalid Date'
                    ? `Last update date: ${uploadTimeStamp}`
                    : ''}
                </Typography>
                <Box
                  width={'calc(100% + 22px)'}
                  height={'1px'}
                  display={{xxs: 'flex', md: 'none'}}
                  marginLeft={'-11px'}
                  sx={{
                    backgroundColor: '#7E7E7E20',
                    mb: {xxs: '8px', md: '0px'},
                    mt: {xxs: '5px', md: '0px'},
                  }}
                />

                <Box
                  display={'flex'}
                  justifyContent={'flex-start'}
                  alignItems={'center'}
                  gap={1}
                >
                  <ButtonV2
                    component={'label'}
                    disableRipple
                    color="primary"
                    onClick={() => {
                      hiddenFileReInput?.current?.click();
                      setEnableCta(true);
                      setButtonEnable(true);
                    }}
                    sx={{
                      border: '0px',
                      fontSize: 12,
                      fontWeight: 500,
                      lineHeight: '20px',
                      padding: 0,
                      ':hover': {
                        backgroundColor: '#FFF',
                      },
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {newUploadText}
                    <UploadInput
                      ref={hiddenFileReInput}
                      handleUpload={handleFileUpload}
                      accept={allowedFileFormat}
                    />
                  </ButtonV2>
                  <Box
                    display={enableCta ? 'none' : 'flex'}
                    justifyContent={'center'}
                  >
                    <Typography
                      component={'label'}
                      color="primary.main"
                      variant="caption"
                      // lineHeight={'10px'}
                      marginRight={enableCta ? '0px' : '9px'}
                    >
                      |
                    </Typography>
                    <Typography
                      component={'label'}
                      color="primary.main"
                      variant="subtitle3"
                      lineHeight="16px"
                      sx={{
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        setEnableCta(true);
                        setOpenPanel3(false);
                        setOpenPanel1(true);
                        setUbaTrigger(true);
                      }}
                      whiteSpace="nowrap"
                    >
                      Its my updated resume
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        ) : (
          <Box>
            <Box
              display="flex"
              mt={1}
              mb={1}
              alignItems={'center'}
              justifyContent={'flex-start'}
              gap={2.25}
            >
              <ButtonV2
                disableRipple
                variant="outlined"
                color="primary"
                component="label"
                sx={{
                  justifyContent: 'flex-start',
                  width: {xxs: 180, md: 260},
                  flexShrink: 0,
                  height: 40,
                  textAlign: 'left',
                  cursor: 'pointer',
                  lineHeight: '16px',
                  minWidth: 0,
                  fontSize: 14,
                  fontWeight: 500,
                  padding: 1.5,
                  ':hover': {
                    textDecoration: 'none',
                    backgroundColor: 'transparent',
                  },
                }}
              >
                {buttonText}
                <UploadInput
                  ref={hiddenFileInput}
                  handleUpload={handleFileUpload}
                  accept={allowedFileFormat}
                />
              </ButtonV2>
              <Link
                variant="subtitle2"
                color={error === 'nofile' ? 'error.main' : '#7E7E7E !important'}
                onClick={checkAndDownloadFile}
                sx={{
                  padding: 0,
                  lineHeight: '16px',
                  minWidth: 0,
                  flex: 1,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  ':hover': {
                    textDecoration: 'underline',
                    cursor: file ? 'pointer' : 'not-allowed',
                    color:
                      error === 'nofile' ? 'error.main' : '#7E7E7E !important',
                  },
                }}
              >
                {loading ? (
                  <Loader
                    size={16}
                    sx={{
                      color: '#7E7E7E',
                    }}
                  />
                ) : (
                  `(${
                    file && fileName
                      ? defaultFileName?.length
                        ? defaultFileName
                        : fileName?.length > 15
                          ? truncateString(fileName, 15)
                          : fileName
                      : NO_FILE_NAME
                  })`
                )}
              </Link>
            </Box>
            <Typography
              variant="body3"
              color={
                ['filesize', 'invalid'].includes(error)
                  ? 'error.main'
                  : 'text.secondary'
              }
              lineHeight={'18px'}
            >
              {alertMessage}
            </Typography>
            {error && !['nofile', 'filesize', 'invalid'].includes(error) && (
              <Typography
                variant="body3"
                color="error"
                sx={{
                  display: 'block',
                  marginTop: 0.5,
                }}
              >
                {error}
              </Typography>
            )}
          </Box>
        )
      ) : (
        <Box
          width={'100%'}
          display={'flex'}
          justifyContent={'flex-start'}
          alignItems={'flex-start'}
          py={2}
          px={2.375}
          border={'1px solid #E5E5E5'}
          borderRadius={8}
          mt={1}
          gap={1.375}
          height={70}
        >
          <Skeleton variant="rounded" width={24} height={24}></Skeleton>
          <Box
            display={'flex'}
            flexDirection={'column'}
            justifyContent={'flex-start'}
            alignItems={'flex-start'}
            gap={1.5}
            width={'100%'}
          >
            <Skeleton variant="rounded" width={'50%'} height={8}></Skeleton>
            <Skeleton variant="rounded" width={'100%'} height={10}></Skeleton>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default UploadFileV2;
